import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import { API } from '../../apis/api';
import {
  API_RESOURCE_URLS,
  ASN_STATUS,
  MESSAGE_TYPE,
  DATE_FORMAT,
  USER_OPERATIONS,
  RESOURCE_TYPE,
  ASN_SELECTOR_HEADER_TEXT,
  DISPLAY_MESSAGES,
  APPLICATION_VARIANTS,
} from '../../constants';
import { reasons, agencies } from '../../staticData';
import { useHistory } from 'react-router-dom';
import defaultStyles from './ChassisLineOn.module.css';
import jlrStyles from './ChassisLineOnJlr.module.css';
import {
  AggregateStatusIndicator,
  AsnSelectorDisplay,
  DropToolBar,
  BackButton,
} from '..';
import { Popover } from '@material-ui/core';
import { AGGREGATE_STATUS } from '../../constants';
import { statusStringMap } from '../../staticData';
import {
  loadAsnDates,
  compareByDateAndSerialNumber,
  getRelativeDateString,
  formatDataToIncludeColour,
  getAggregateStatusAndModifiedAtAndComment,
} from '../../utils/tableUtils';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import {
  buildErrorMessage,
  fetchAllLinedOutAsnsForPlantAndLine,
  getLastAsnGeneratedDateForPlantAndLine,
} from '../../apis/calls';
import { VcStatusPopup, CommentFormModal } from '../';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { withAllowedOperationsProvider } from '../../hocs';
import { downloadCsv, getDatesForRange, setStylesAccordingToPlant } from '../../utils/utils';
import Table from '../Table/Table';
import ForwardIcon from '@material-ui/icons/Forward';
import config from '../../config/config';

const LineoutDataDisplay = ({ rowData }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const ref = useRef(null);
  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  return (
    <>
      <div ref={ref} onClick={openPopup}>
        {statusStringMap[ASN_STATUS.LINE_OUT]}
      </div>
      <Popover
        open={isPopupOpen}
        anchorEl={ref.current}
        onClose={closePopup}
        onMouseLeave={closePopup}
        anchorOrigin={{
          vertical: 'right',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'left',
          horizontal: 'left',
        }}
      >
        <VcStatusPopup asn={rowData} />
      </Popover>
    </>
  );
};

const ChassisLineOn = ({
  hasActiveEdits,
  setHasActiveEdits,
  handleCountChange,
}) => {
  const ROW_HEIGHT = 38;
  const today = moment();
  const {showPopup, showLoading, closePopUp} = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;
  const [styles, setStyles] = useState(defaultStyles);
  const latestLineValueRef = useRef(line.value);
  const latestPlantValueRef = useRef(plant.value);
  const [lastAsnGeneratedDate, setLastAsnGeneratedDate] = useState(null);
  const history = useHistory();
  const [activeDate, setActiveDate] = useState(null);
  const [asnData, setAsnData] = useState({
    data: [],
    isLoading: false,
    error: null,
  });
  const [asnCount, setAsnCount] = useState(0);

  const [isCommentFormModalOpen, setIsCommentFormModalOpen] = useState(false);
  const openCommentFormModal = () => setIsCommentFormModalOpen(true);
  const closeCommentFormModal = () => setIsCommentFormModalOpen(false);
  const { APPLICATION_VARIANT } = config;

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [asnDates, setAsnDates] = useState([]);
  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [asnToLineOn, setAsnToLineOn] = useState(null);
  const openSelectorPopup = () => setIsSelectorOpen(true);
  const closeSelectorPopup = () => setIsSelectorOpen(false);
  const fixedAsnSelectorColumns = [
    {
      width: 50,
      title: '',
      field: null,
      render: row => {
        if (row.asnId === asnToLineOn.asnId)
          return (
            <ForwardIcon
              data-testid={'asn-to-line-on'}
              className={styles.Forwerd}
            />
          );
        return null;
      },
    },
    {
      width: 150,
      title: 'ASN',
      field: 'asn',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 120,
      title: 'VC',
      field: 'vc',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 280,
      title: 'Description',
      field: 'vcDescription',
      style: {
        textAlign: 'left',
      },
    },
    {
      width: 130,
      title: 'VC Status',
      field: 'status',
      style: {
        textAlign: 'center',
      },
    },
  ];
  const fixedColumns = [
    {
      width: 120,
      title: 'ASN',
      field: 'asn',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'VC',
      field: 'vc',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: 'Description',
      field: 'vcDescription',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'Shift',
      field: 'shift',
      enableSearch: true,
      enableFilter: true,
      render: ({ shiftName }) => (
        <div style={{ textAlign: 'center' }}>
          {shiftName && <div className={styles.shift}>{shiftName}</div>}
        </div>
      ),
    },
    {
      width: 160,
      title: 'Planned FDT',
      field: 'plannedDropTimestamp',
      render: row =>
        row.plannedDropTimestamp
          ? moment(row.plannedDropTimestamp).format('hh:mm A DD/MM/YYYY')
          : null,
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: 'VC Status',
      field: 'vcStatus',
      render: row =>
        row.edited ? 'Pending' : <LineoutDataDisplay rowData={row} />,
      enableSearch: true,
      enableFilter: true,
    },
    (APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4 ? {
      width: 160,
      title: 'Aggregate Status',
      field: 'aggregate',
      render: row => <AggregateStatusIndicator rowData={row} />,
    } : null),
    {
      width: 160,
      title: 'Planned Date',
      field: 'originalProductionDate',
      render: row => moment(row.originalProductionDate).format('DD MMMM YYYY'),      
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 130,
      title: 'Ageing',
      field: 'ageing', //dummy field
      render: row =>
        getRelativeDateString(moment(row.originalProductionDate, 'YYYY-MM-DD')),
    },

    {
      width: 150,
      title: 'Lined-Out on',
      field: 'linedOutSince',
      render: row => moment(row.linedOutSince).format('DD MMMM YYYY'),
    },
  ].filter(c => c);

  const vcColoumns = [
    {
      width: 200,
      title: 'PowerSteering',
      field: 'powerSteering',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'BC No.',
      field: 'buildCardNumber',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'Defence',
      field: 'defence',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 120,
      title: 'Market',
      field: 'market',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: 'Category',
      field: 'category',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: 'Country',
      field: 'country',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 110,
      title: 'FBV',
      field: 'fbv',
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: 'Colour',
      field: 'colour',
      enableSearch: true,
      enableFilter: true,
    },
    {
          width: 140,
          title: 'Major Model',
          field: 'majorModel',
          enableSearch: true,
          enableFilter: true,
    },
  ];
  const [archiveAsnDetails, setArchiveAsnDetails] = useState([]);
  const { showError } = usePopupManager();
  const setCount = count => {
    setAsnCount(count);
  };
  // lineon action functions
  const handleDropSelection = row => {
    setAsnToLineOn(row[0]);
    openSelectorPopup();
  };

  const handleArchiveAsns = filteredRows => {
    setArchiveAsnDetails(filteredRows);
    openCommentFormModal();
  };

  const checkIfArchiveIsAvailable = selectedRows =>
    selectedRows.every(row => moment(row.originalProductionDate).isSameOrBefore(today));

  const archiveAsn = async rows => {
    try {
      const payload = rows.map(({ asnId, reason, agency, comment }) => ({
        asnId: asnId,
        reason: reason,
        agency: agency,
        comment: comment,
      }));
      await API.patch(API_RESOURCE_URLS.VC_ASN_BULK, {
        updates: payload,
      });
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to archive ASN`,
        error: error,
      });
    }
  };
  const handleArchiveAsn = (rows, reason, agency, comment) => {
    const editedRows = rows.map(row => ({
      ...row,
      reason,
      agency,
      comment,
    }));
    return archiveAsn(editedRows).then(() => {
      loadLinedOutVcAsnOfSelectedPlandAndLineForDate(activeAsnDate);
    });
  };
  const compareByOriginalDateAndAsnId = (a, b) => {
    const originalPlannedDateOfA = moment(a.originalProductionDate, 'YYYY-MM-DD');
    const originalPlannedDateOfB = moment(b.originalProductionDate, 'YYYY-MM-DD');
    if (originalPlannedDateOfA.isBefore(originalPlannedDateOfB, 'day'))
      return -1;
    if (originalPlannedDateOfA.isAfter(originalPlannedDateOfB, 'day')) return 1;
    return a.asnId.localeCompare(b.asnId);
  };

  const addFlagsFormatAndSortDataForRender = (linedOutAsns, date) => {
    if (linedOutAsns.length === 0) return [];

    let sortBy = (a, b) =>
      compareByDateAndSerialNumber(a, b, 'date', 'serialNumber');
    if (date.isBefore(moment(), 'day'))
      // when showing all asns, sort by asn as we show original plan date  in the cols
      sortBy = compareByOriginalDateAndAsnId;

    const rows = linedOutAsns.map(asn => {
      const updatedMaterialGroupClusters = asn.materialGroupClusters.map(
        ({ name, materials }) => ({
          name,
          materials: materials.map(material =>
            getAggregateStatusAndModifiedAtAndComment(material),
          ),
        }),
      );

      const hasUnAvailableAggregates = updatedMaterialGroupClusters
        .map(group => group.materials[0].aggregateStatus)
        .some(aggregateStatus => aggregateStatus === AGGREGATE_STATUS.LINE_OUT);

      return {
        ...asn,
        materialGroupClusters: [...updatedMaterialGroupClusters],
        vcStatus: statusStringMap[asn.status],
        hasUnAvailableAggregates,
        id: asn.asnId,
      };
    });
    rows.sort(sortBy);
    return rows;
  };

  const redirectToApplicationLandingPage = () => {
    history.push('/');
  };

  const getLinedOutAsnsForPlantAndLineBasedOnDate = (
    selectedPlant,
    selectedChassisLine,
    date,
  ) => {
    if (date.isBefore(today, 'day')) {
      return fetchAllLinedOutAsnsForPlantAndLine(
        selectedPlant,
        selectedChassisLine,
      ).then(res => res.data);
    }
    const dateString = date.format(DATE_FORMAT.ISO);
    return API.get(API_RESOURCE_URLS.getLinedOutChassis(selectedPlant, selectedChassisLine), {
      params: {
        lastPlannedDate: dateString,
      },
    })
      .then(res => res.data)
      .then(asns => asns.filter(asn => asn.status === ASN_STATUS.LINE_OUT));
  };

  const loadLinedOutVcAsnOfSelectedPlandAndLineForDate = async date => {
    if (!date)
      return setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
    try {
      setRowsData(prevState => ({ ...prevState, loading: true }));
      const linedOutAsns = await getLinedOutAsnsForPlantAndLineBasedOnDate(
        plant.value,
        line.value,
        date,
      );
      const data = formatDataToIncludeColour(linedOutAsns);
      if (
        plant.value !== latestPlantValueRef.current ||
        line.value !== latestLineValueRef.current
      )
        return setRowsData(prevState => ({ ...prevState, loading: false }));

      const processedData = addFlagsFormatAndSortDataForRender(data, date);
      setRowsData({
        loading: false,
        data: processedData.map(item => ({
          ...item,
          vcStatus: statusStringMap[item.status],
        })),
        filteredData: processedData.map(item => ({
          ...item,
          vcStatus: statusStringMap[item.status],
        })),
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({ type: MESSAGE_TYPE.INTERNAL_ERROR, error: err });
      setRowsData(prevState => ({ ...prevState, loading: false }));
    }
  };

  const prepareJsonHeaderAndValueFormats = aggregatePartNames => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      partName => ({
        label: partName + ' Status',
        value: row =>
          row.materialGroupClusters.filter(group => group.name === partName)[0]
            ?.materials[0]?.aggregateStatus || 'unavailable',
      }),
    );

    const vcHeaderAndValueFormats = [
      {
        label: 'ASN',
        value: row => row.asn,
      },
      {
        label: 'VC',
        value: row => row.vc,
      },
      {
        label: 'VC Description',
        value: row => row.vcDescription?.replace(/[\n\r,]/g, ''),
      },
      {
        label: 'Planned On',
        value: row => row.originalProductionDate,
      },
      {
        label: 'Lined Out Since (IST)',
        value: row => moment(row.linedOutSince).format('YYYY-MM-DD hh:mm:ss'),
      },
      {
        label: 'Lined Out Reason',
        value: row => row.lineOutReason,
      },
      {
        label: 'Lined Out Agency',
        value: row => row.lineOutAgency,
      },
      {
        label: 'Lined Out Comment',
        value: row => row.lineOutComment,
      },

      {
        label: 'Family',
        value: row => row.family,
      },
      {
        label: 'Order Frequency',
        value: row => row.orderFrequency,
      },
      (APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4 ?
        (
          {
            label: 'Power Steering',
            value: row => row.powerSteering,
          },
          {
            label: 'Build Card Number',
            value: row => row.buildCardNumber,
          },
          {
            label: 'Market',
            value: row => row.market,
          },
          {
            label: 'Category',
            value: row => row.category,
          },
          {
            label: 'Country',
            value: row => row.country,
          },
          {
            label: 'Defence',
            value: row => row.defence,
          },
          {
            label: 'FBV',
            value: row => row.fbv,
          },

          {
            label: 'FBV Vendor Code',
            value: row => row.fbvVendorCode,
          },
          {
            label: 'FBV Vendor Name',
            value: row => row.fbvVendorName,
          }
        ) : null),
      {
        label: 'Line',
        value: row => row.line,
      },
      {
        label: 'Shift',
        value: row => row.shiftName,
      },
      {
        label: 'Planned FDT',
        value: row =>
          row.plannedDropTimestamp
            ? moment(row.plannedDropTimestamp).format('hh:mm A DD/MM/YYYY')
            : null,
      },
      {
        label: 'Major Model',
        value: row => row.majorModel,
      },
    ].filter(c => c);

    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadLineoutCsvReport = filteredData => {
    try {
      if (!rowsData.loading) {
        const dateValue = activeAsnDate.isBefore(
          moment().format(DATE_FORMAT.ISO),
        )
          ? 'AllDates'
          : activeAsnDate.format('YYYYMMDD');

        const jsonData = filteredData;

        const aggregatePartNames =
          rowsData.data[0]?.materialGroupClusters.map(group => group.name) ||
          [];

        const { parseAsync } = require('json2csv');
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields, quote: '' };
        parseAsync(jsonData, opts)
          .then(csv => {
            const exportedFilename =
              'LineOutReport_' +
              plant.label +
              '_' +
              line.label +
              '_' +
              dateValue +
              '.csv';
            downloadCsv(csv, exportedFilename);
          })
          .catch(error =>
            console.error(
              'Failed to download lineout report, error:' +
              buildErrorMessage(error),
            ),
          );
      }
    } catch (error) {
      console.error(
        'Failed to download lineout report, error:' + buildErrorMessage(error),
      );
    }
  };

  useEffect(() => {
    setStylesAccordingToPlant(plant, setStyles, defaultStyles, jlrStyles);
  }, [plant]);

  useEffect(() => {
    handleCountChange(asnCount);
  }, [asnCount, handleCountChange]);

  useEffect(() => {
    latestLineValueRef.current = line.value;
    latestPlantValueRef.current = plant.value;

    loadAsnDates(
      plant.value,
      line.value,
      setAsnDates,
      setActiveAsnDate,
      moment().subtract(1, 'days'),
      latestPlantValueRef,
      latestLineValueRef,
    ) // so that default is View all.
      .catch(err => {
        //TODO: handleFailure
        console.error(buildErrorMessage(err));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  useEffect(() => {
    setHasActiveEdits(isSelectorOpen);
  }, [isSelectorOpen, setHasActiveEdits]);

  const processAsnData = asns => {
    if (asns.length === 0) return [];
    const rows = asns.map(asn => ({ ...asn, id: asn.asn }));
    rows.sort((a, b) => a.serialNumber - b.serialNumber);
    return rows;
  };

  const loadDropSequenceVcAsnForDate = async date => {
    if (!date) return null;
    try {
      setAsnData(prevState => ({ ...prevState, loading: true }));
      const dateString = date.format(DATE_FORMAT.ISO);
      const response = await API.get(API_RESOURCE_URLS.VC_ASN, {
        params: {
          from: dateString,
          to: dateString,
          line: line.value,
          plant: plant.value,
          status: [
            ASN_STATUS.PLANNED,
            ASN_STATUS.DROPPED,
            ASN_STATUS.READY_FOR_DROP,
            ASN_STATUS.ROLLED_OUT,
            ASN_STATUS.SFLT,
            ASN_STATUS.WTCL,
          ],
        },
      });

      const processedData = processAsnData(response.data);
      const data = formatDataToIncludeColour(processedData);
      return setAsnData({
        loading: false,
        data,
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showPopup({ type: MESSAGE_TYPE.INTERNAL_ERROR, error: err });
      return setAsnData(prevState => ({ ...prevState, loading: false }));
    }
  };

  const sortBySerialNumber = (a, b) => {
    if (a.serialNumber > b.serialNumber) return 1;
    if (a.serialNumber < b.serialNumber) return -1;
    return 0;
  };

  const getLastAsnInRolledOrSfltOrWtclState = (allAsns, lastAsn) => {
    const sortedAsns = allAsns.sort(sortBySerialNumber);
    const pendingAsn = sortedAsns.find(asn => asn?.asnId === lastAsn?.asnId);
    let nextAsnInHappyStatus = null;
    let serialNumber = pendingAsn?.serialNumber;
    let nextBookedAsn = null;
    sortedAsns.forEach(asn => {
      if (
        asn?.serialNumber >= serialNumber + 1 &&
        (asn?.status === ASN_STATUS.ROLLED_OUT ||
          asn?.status === ASN_STATUS.WTCL ||
          asn?.status === ASN_STATUS.SFLT)
      ) {
        nextBookedAsn = asn;
        serialNumber = serialNumber + 1;
      }
      if (nextBookedAsn) {
        nextAsnInHappyStatus = nextBookedAsn;
        return nextBookedAsn;
      } else {
        nextAsnInHappyStatus = pendingAsn;
        return pendingAsn;
      }
    });
    return nextAsnInHappyStatus;
  };

  const handleLineOn = async (asnData, date) => {
    const formattedDate = date.format(DATE_FORMAT.ISO);
    try {
      const selectedPositionIndex = asnData.data.findIndex(
        asn => asn.isToBeLinedOn,
      );
      const asnBeforeSelectedAsnInPendingState =
        asnData.data[selectedPositionIndex - 1];

      const response = await API.get(API_RESOURCE_URLS.VC_ASN, {
        params: {
          from: formattedDate,
          to: formattedDate,
          line: line.value,
          plant: plant.value,
          status: [
            ASN_STATUS.READY_FOR_DROP,
            ASN_STATUS.DROPPED,
            ASN_STATUS.PLANNED,
            ASN_STATUS.ROLLED_OUT,
            ASN_STATUS.WTCL,
            ASN_STATUS.SFLT,
          ],
        },
      });

      const { data: allAsns } = response;
      const lastAsnInRolledOrSfltOrWtclState = getLastAsnInRolledOrSfltOrWtclState(
        allAsns,
        asnBeforeSelectedAsnInPendingState,
      );
      showLoading();
      await API.patch(API_RESOURCE_URLS.VC_LINEON, {
        date: activeDate.format(DATE_FORMAT.ISO),
        position: lastAsnInRolledOrSfltOrWtclState?.serialNumber + 1 || 1,
        asnId: asnToLineOn.asnId,
      });
      loadLinedOutVcAsnOfSelectedPlandAndLineForDate(activeAsnDate);
      closePopUp();
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: `ASN ${asnToLineOn.asn} lined-on.`,
        info: '',
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      if (activeDate)
        showPopup({
          error: err,
          contextText: DISPLAY_MESSAGES.ASN_LINEON_FAILED,
        });
      else
        showPopup({
          error: new Error('Please generate ASN for next day to line on'),
          contextText: DISPLAY_MESSAGES.ASN_LINEON_FAILED,
        });
    }
  };
  const lastRefreshedDate = useAjaxCallbackWithIntervals(
    rowsData.loading,
    hasActiveEdits,
    loadLinedOutVcAsnOfSelectedPlandAndLineForDate,
    activeAsnDate,
    line.value,
    plant.value,
  );
  useEffect(() => {
    if (isSelectorOpen) loadDropSequenceVcAsnForDate(activeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate, isSelectorOpen]);

  useEffect(() => {
    getLastAsnGeneratedDateForPlantAndLine(plant.value, line.value)
      .then(lastAsnGeneratedDateData => {
        const dates = getDatesForRange(moment(), lastAsnGeneratedDateData);
        setActiveDate(dates[0]);
        setLastAsnGeneratedDate(lastAsnGeneratedDateData);
      })

      .catch(err => {
        console.error(buildErrorMessage(err));
        showPopup({ error: err, contextText: 'Failed to get asn dates' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  return (
    <>
      <div className={styles.backButton}>
        <BackButton
          basePath="PPC"
          action="Line Up"
          handleClick={redirectToApplicationLandingPage}
        />
      </div>

      <DropToolBar
        activeDate={activeAsnDate}
        dates={asnDates}
        handleDateSelect={date => setActiveAsnDate(date)}
        selectedOptionalColNames={selectedOptionalColNames}
        applyColumnsSelection={selectedColName =>
          setSelectedOptionalColNames(selectedColName)
        }
        lastRefreshedDate={lastRefreshedDate}
        contextualButtonProps={{
          className: clsx(
            moment().subtract(1, 'days').isSame(activeAsnDate, 'day')
              ? styles.greenRoundButton
              : styles.whiteRoundButton,
          ),
          text: 'ALL',
          label: 'View',
          handleClick: () => setActiveAsnDate(moment().subtract(1, 'days')),
        }}
        restrictNavigationDuringEditing
        hasActiveEdits={hasActiveEdits}
      />
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: '100%' }}>
          <Table
            columns={APPLICATION_VARIANT === APPLICATION_VARIANTS.IPMS4 ? [
              ...fixedColumns.filter(column => {
                const yesterday = moment().subtract(1, 'days');
                // for specific date view
                if (!yesterday.isSame(activeAsnDate, 'day'))
                  return (
                    column.field !== 'originalDate' && column.field !== 'ageing'
                  );
                //for all view
                return column.field !== 'linedOutSince';
              }),
              ...vcColoumns,
            ] :
              [
                ...fixedColumns.filter(column => {
                  const yesterday = moment().subtract(1, 'days');
                  // for specific date view
                  if (!yesterday.isSame(activeAsnDate, 'day'))
                    return (
                      column.field !== 'originalDate' && column.field !== 'ageing'
                    );
                  //for all view
                  return column.field !== 'linedOutSince';
                }),
              ]
            }
            defaultColumns={[
              ...fixedColumns.filter(column => {
                const yesterday = moment().subtract(1, 'days');
                // for specific date view
                if (!yesterday.isSame(activeAsnDate, 'day'))
                  return (
                    column.field !== 'originalDate' && column.field !== 'ageing'
                  );
                //for all view
                return column.field !== 'linedOutSince';
              }),
            ]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.data}
            secondaryActions={[
              {
                name: 'ARCHIVE',
                backgroundColor: '#F17C7C',
                authOperation: USER_OPERATIONS.DELETE_CHASSIS_PLAN_ASN,
                shouldEnable: selectedRows => {
                  if (selectedRows.length)
                    return checkIfArchiveIsAvailable(selectedRows);
                  return false;
                },
                actionFn: filteredRows => handleArchiveAsns(filteredRows),
              },
              {
                name: 'DOWNLOAD CSV',
                getFilteredData: true,
                authOperation: USER_OPERATIONS.GET_CHASSIS_PLAN_ASN,
                shouldEnable: () => rowsData.data.length > 0,
                actionFn: filteredData =>
                  generateAndDownloadLineoutCsvReport(filteredData),
              },
            ]}
            primaryAction={{
              name: 'LINE ON',
              getFilteredData: true,
              authOperation: USER_OPERATIONS.UPDATE_CHASSIS_PLAN_ASN,
              shouldEnable: selectedRows => {
                if (selectedRows.length === 1) return true;
              },
              actionFn: filteredData => handleDropSelection(filteredData),
            }}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
          />
        </div>
      </div>
      <div className={styles.footer}></div>

      <AsnSelectorDisplay
        open={isSelectorOpen}
        handleClose={closeSelectorPopup}
        asnData={asnData}
        setAsnData={setAsnData}
        updateRows={() =>
          loadLinedOutVcAsnOfSelectedPlandAndLineForDate(activeAsnDate)
        }
        handleLineOnSubmit={handleLineOn}
        asnToLineOn={asnToLineOn}
        lastAsnOrAgsnGeneratedDate={lastAsnGeneratedDate}
        activeDate={activeDate}
        setActiveDate={setActiveDate}
        fixedColumns={fixedAsnSelectorColumns}
        asnSelectorheaderText={ASN_SELECTOR_HEADER_TEXT}
        selectedLine={line.label}
      />

      {isCommentFormModalOpen ? (
        <CommentFormModal
          open={isCommentFormModalOpen}
          handleClose={closeCommentFormModal}
          title="Archive"
          rowsData={archiveAsnDetails}
          reasons={reasons}
          agencies={agencies}
          lineBackgroundColor="#F17C7C"
          handleSubmit={handleArchiveAsn}
        />
      ) : null}
    </>
  );
};

ChassisLineOn.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  handleCountChange: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  ChassisLineOn,
  false,
  RESOURCE_TYPE.CHASSIS,
);
